import { FRAGMENT_SHADER, VERTEX_SHADER } from './shaders.js';
export class GlInitError extends Error {
    constructor(message) {
        super(message);
        this.name = 'GlInitError';
    }
}
/** WebGL2 が使えるか。**副作用でコンテキストを作らない**軽い判定。 */
export function isWebGl2Supported() {
    if (typeof document === 'undefined')
        return false;
    // `WebGL2RenderingContext` の存在だけでは実際に取得できるとは限らないため、
    // 使い捨ての canvas で試す。取得したコンテキストはすぐ捨てる。
    try {
        const probe = document.createElement('canvas');
        const gl = probe.getContext('webgl2');
        if (gl === null)
            return false;
        gl.getExtension('WEBGL_lose_context')?.loseContext();
        return true;
    }
    catch {
        return false;
    }
}
/**
 * コンテキストを取得する。
 *
 * `premultipliedAlpha: false` にしているのは、動画のアルファを扱わないため
 * 余計な合成をさせないほうが素直に出るから。`alpha: true` は canvas の下の
 * 背景画像を透かすのに必要。
 *
 * **`stencil: true` は内向きの曲面の隠れに使う**(#361 / [ADR-0060](../../../docs/adr/0060-curved-surface.md) の決定 5)。
 * 手前の裏の片をステンシルに書き、奥の表の片をその外だけに描く([`curve-draw.ts`](./curve-draw.ts))。
 * 平面・折れた枠は使わない。書き出し([`compose.ts`](./compose.ts))のコンテキストも同じ。
 */
export function createContext(canvas) {
    const gl = canvas.getContext('webgl2', {
        alpha: true,
        premultipliedAlpha: false,
        antialias: true,
        stencil: true,
        // タブ復帰時にバッファを保持しないほうがメモリを食わない。毎フレーム描く。
        preserveDrawingBuffer: false,
        // 埋め込み先で GPU を占有しすぎないよう既定に任せる。
        powerPreference: 'default',
    });
    if (gl === null)
        throw new GlInitError('WebGL2 コンテキストを取得できません');
    return gl;
}
function compile(gl, type, source) {
    // `createShader` の型は元から nullable(他の create* と違って)。
    const shader = gl.createShader(type);
    if (shader === null)
        throw new GlInitError('シェーダを作成できません');
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (gl.getShaderParameter(shader, gl.COMPILE_STATUS) !== true) {
        const log = gl.getShaderInfoLog(shader) ?? '(詳細不明)';
        gl.deleteShader(shader);
        throw new GlInitError(`シェーダのコンパイルに失敗しました: ${log}`);
    }
    return shader;
}
/**
 * プログラムを作る。**ロスト後の再構築でも同じ関数を使う。**
 */
export function createProgram(gl) {
    // **null チェックを残す。** `lib.dom.d.ts` の型は non-null だが、WebGL の
    // 仕様では**コンテキストが失われている間 null を返す**。型が現実に追いついて
    // いないだけで、ロスト中に呼ぶ経路(復帰処理)が実際にある。
    const program = gl.createProgram();
    if (program === null)
        throw new GlInitError('プログラムを作成できません');
    const vertex = compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
    const fragment = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    // リンク後はシェーダ本体を保持する必要がない。
    gl.deleteShader(vertex);
    gl.deleteShader(fragment);
    if (gl.getProgramParameter(program, gl.LINK_STATUS) !== true) {
        const log = gl.getProgramInfoLog(program) ?? '(詳細不明)';
        gl.deleteProgram(program);
        throw new GlInitError(`プログラムのリンクに失敗しました: ${log}`);
    }
    // 同じ理由(ロスト中は null)。
    const vao = gl.createVertexArray();
    const positionBuffer = gl.createBuffer();
    if (vao === null || positionBuffer === null) {
        gl.deleteProgram(program);
        throw new GlInitError('頂点バッファを作成できません');
    }
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    const position = gl.getAttribLocation(program, 'aPosition');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);
    return {
        gl,
        program,
        vao,
        positionBuffer,
        uniforms: {
            resolution: gl.getUniformLocation(program, 'uResolution'),
            dpr: gl.getUniformLocation(program, 'uDpr'),
            inverse: gl.getUniformLocation(program, 'uInverse'),
            panelSize: gl.getUniformLocation(program, 'uPanelSize'),
            contentRect: gl.getUniformLocation(program, 'uContentRect'),
            texture: gl.getUniformLocation(program, 'uTexture'),
            highlight: gl.getUniformLocation(program, 'uHighlight'),
        },
    };
}
/**
 * 動画用のテクスチャを作る。
 *
 * 動画は 2 の冪サイズとは限らないので **mipmap を使わず `CLAMP_TO_EDGE`**。
 * `REPEAT` のままだと縁で反対側の色を拾う。
 */
export function createSourceTexture(gl) {
    // ロスト中は null(型は non-null だが仕様上ありうる)。
    const texture = gl.createTexture();
    if (texture === null)
        throw new GlInitError('テクスチャを作成できません');
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    return texture;
}
/** プログラムと付随リソースを破棄する。 */
export function deleteProgram(p) {
    p.gl.deleteVertexArray(p.vao);
    p.gl.deleteBuffer(p.positionBuffer);
    p.gl.deleteProgram(p.program);
}
//# sourceMappingURL=gl-context.js.map